import { notificationSuccess } from 'app/core/toast';
import { useGetApplication } from 'app/hooks';
import { useSpinnerActions } from 'app/layouts/store';
import { type FormSectionResponse } from 'app/modules/TaxStatement/FormVersion/types';
import i18next from 'i18next';
import { useNavigate } from 'react-router-dom';

import type Paginated from '../../../core/paginable';
import { messages, messagesKey } from '../../translation';
import { baseUrl as baseFormVersionUrl } from '../FormVersion/services/formVersion.service';

import { createFormCell, deleteFormCell, updateFormCell } from './services/formCells.service';
import { type FormCellRequest } from './formCellRequest';
import { baseUrl } from './http';
import { label } from './translation';
import { type FormCell, type FormCellsGroupBySection } from './types';

export const useFormCell = (id: string) => {
  const [{ data, loading, error, response }] = useGetApplication<Paginated<FormCell>>({
    url: baseUrl,
    params: {
      id,
      page: 1,
      pageSize: 1
    }
  });

  const [form] = data?.data || [];

  return { loading, data: form, error, response };
};

export const useFormCells =
  // : (formVersionId: string, ignoreDefaultLang?: boolean) => FormCell[]
  (formVersionId: string, ignoreDefaultLang?: boolean) => {
    return useGetApplication<Array<FormCell>>({
      url: baseUrl + '/',
      params: { formVersionId: formVersionId },
      headers: ignoreDefaultLang ? { lang: true } : undefined
    });
  };

export const useFormCellsPerSection =
  // : (formVersionId: string, ignoreDefaultLang?: boolean) => FormCell[]
  (formVersionId: string, ignoreDefaultLang?: boolean) => {
    const [{ data, loading, error, response }] = useGetApplication<Array<FormCell>>({
      url: baseUrl + '/',
      params: { formVersionId: formVersionId },
      headers: ignoreDefaultLang ? { lang: true } : undefined
    });

    const sections: Array<FormCellsGroupBySection> = [];
    // formatear el data
    if (data) {
      // cuando ya tengo los datos
      const celdas: Array<FormCell> = data;
      // se extraen las secciones y se ordenan
      celdas.forEach((celda: FormCell) => {
        // busco si tenemos la seccion
        const seccion: FormCellsGroupBySection | undefined = sections.find(
          (item: FormCellsGroupBySection) => item?.section?.id === celda.section?.id
        );
        // si la encuentra, se agrega la celda para esa seccion
        if (seccion) {
          seccion.cells.push(celda);
        } else {
          // entonces hay que agregar una nueva
          sections.push({
            section: celda?.section,
            cells: [celda]
          });
        }
      });
      // lo ultimo  es ordenarlo por el orden de las secciones
      sections.sort((a, b) => (a.section?.order || 0) - (b.section?.order || 0));
    }

    return { sections, loading, error, response };
  };

export const useFormCellById = (formVersionId: string, formCellId: string) => {
  const [{ data, loading, error, response }] = useGetApplication<Array<FormCell>>({
    url: baseUrl + '/',
    params: { formVersionId: formVersionId, id: formCellId }
  });

  const [formCell] = data || [];

  return { loading, error, response, formCell };
};

export const useFormCellTypes = () => {
  const types = ['numeric', 'currency', 'date', 'list', 'text'];
  return types.map((type) => ({
    id: type,
    name: label(i18next.t.bind(i18next), type)
  }));
};

export const useFormCellType = (type: string) => {
  const types = useFormCellTypes();
  return types.find((value) => value.id === type);
};

export const useGetSectionsFromVersion = (id: string) => {
  const [{ data, loading, error, response }] = useGetApplication<Array<FormSectionResponse>>({
    url: `${baseFormVersionUrl}/${id}/sections`,
    params: {}
  });

  return { loading, data, error, response };
};

export const useFormCellsActions = () => {
  const { showSpinner, hideSpinner } = useSpinnerActions();
  const navigate = useNavigate();

  const save = async (formCell: FormCellRequest, redirectPath: string, id?: string) => {
    showSpinner();
    try {
      if (id) {
        await updateFormCell(id, formCell);
      } else {
        await createFormCell(formCell);
      }
      notificationSuccess(messages(messagesKey.savedSucessfully));
      navigate(redirectPath);
    } finally {
      hideSpinner();
    }
  };

  const remove = async (id: string, redirectPath: string) => {
    await deleteFormCell(id);
    notificationSuccess(messages(messagesKey.deletedSucessfully));
    navigate(redirectPath);
  };

  return { save, remove };
};
